import styled from 'styled-components';
import { Card, Row, Button, Transfer } from 'antd';
import { IThemeProps, IToken } from 'store/ducks/globalTypes';
import { IThemeSelectorProps } from 'store/ducks/Global/types';

/* ---------------- COMPONENT ---------------- */

export const Container = styled.div`
  padding: 24px;
  min-height: calc(100vh - 200px);

  .ant-select.ant-select-in-form-item {
    width: auto;
  }
`;

export const HorizontalLayout = styled('div')<IThemeSelectorProps>`
  padding: 0 24px 24px;
`;

export const GridContainer = styled('div')<{ token: any }>`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  justify-items: center;
  width: 100%;

  > a,
  > div {
    width: 100%;
    max-width: 300px;
  }

  .ant-card {
    width: 100%;
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
  }

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  @media (max-width: 400px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

export const RelativeContainer = styled('div')`
  position: relative;
  width: 100%;
  cursor: pointer;
`;

export const ContentWrapper = styled('div')`
  width: 100%;
  padding: 24px;

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

export const TransferCard = styled(Card)<IThemeProps>`
  background: #fff;
  border: 1px solid #52c41a;
  padding: 12px;

  .ant-card-head-title {
    color: ${(props) =>
      props.scheme === 'greenModeTheme' ? 'inherit' : '#000 !important'};
  }

  .ant-transfer {
    display: flex;
    justify-content: center;
    width: 100%;
  }

  .ant-transfer-list {
    width: 100% !important;
    max-width: 400px;
  }

  .ant-select {
    margin-bottom: 12px;
  }

  @media (max-width: 768px) {
    .ant-transfer-list {
      max-width: 100%;
    }
  }
`;

/* ---------------- TYPOGRAPHY ---------------- */

export const Title = styled.h1<{ scheme?: string }>`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 12px;
  color: ${(props) => (props.scheme === 'darkUITheme' ? '#fff' : '#111827')};
`;

export const Description = styled.p<{ scheme?: string }>`
  font-size: 14px;
  margin-bottom: 20px;
  color: ${(props) => (props.scheme === 'darkUITheme' ? '#d1d5db' : '#6b7280')};
`;

/* ---------------- FORM LAYOUT ---------------- */

export const StyledRow = styled(Row)`
  margin-top: 1rem;
`;

export const FormRow = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

/* ---------------- BUTTON ---------------- */

export const ApplyButton = styled(Button)<
  IThemeProps &
    Partial<IToken> & {
      isClientReqPage?: boolean;
      hasDisplayedData?: boolean;
      isCompliancePage?: boolean;
      isComplianceCreationPage?: boolean;
    }
>`
  border-radius: 6px;
  font-weight: 500;

  ${(props) =>
    props.scheme === 'deepNavyTheme' &&
    !props.disabled &&
    props.children === 'Save' &&
    `
    && {
      filter: brightness(1.8) !important;
    }
  `}

  background-color: ${(props) => {
    if (props.type === 'primary') {
      return '#ff7875';
    }
    if (props.scheme === 'darkUITheme' && props.children === 'Copy All') {
      return '#067de4ff';
    }
    // Other buttons only change in eighthTheme
    return props.scheme === 'eighthTheme' ? '#238636 !important' : 'inherit';
  }};
  border-color: ${(props) => {
    if (props.type === 'primary') {
      return '#ff7875';
    }
    // Copy All button in darkUITheme uses blue border
    if (props.scheme === 'darkUITheme' && props.children === 'Copy All') {
      return '#44a7fb';
    }
    return props.scheme === 'eighthTheme' ? '#238636 !important' : 'inherit';
  }};
  color: ${(props) => {
    if (props.type === 'primary') {
      return '#fff !important';
    }
    // Copy All button in darkUITheme uses white text
    if (props.scheme === 'darkUITheme' && props.children === 'Copy All') {
      return '#fff !important';
    }
    // Copy All button in deepNavyTheme uses white text
    if (props.scheme === 'deepNavyTheme' && props.children === 'Copy All') {
      return '#fff !important';
    }
    // Merge selected, Copy selected, Save buttons in deepNavyTheme use black text
    if (
      props.scheme === 'deepNavyTheme' &&
      (props.children === 'Merge selected' ||
        props.children === 'Copy selected' ||
        props.children === 'Save')
    ) {
      return '#000000 !important';
    }
    return props.scheme === 'eighthTheme' ? '#fff !important' : 'inherit';
  }};

  @media (max-width: 1500px) {
    width: 100px !important;
    font-size: ${(props) => {
      if (props.isClientReqPage && props.hasDisplayedData) return '10px';
      return '12px';
    }};
    padding: 4px 8px;
    margin-left: ${(props) => {
      if (props.type === 'primary' && props.children === 'Display') {
        // Move Display button right ONLY before clicking (when hasDisplayedData is false)
        if (
          !props.hasDisplayedData &&
          (props.isCompliancePage || props.isComplianceCreationPage)
        )
          return '150px';
        return '-20px';
      }
      return '0';
    }};
  }

  &:hover {
    background-color: ${(props) => {
      if (props.type === 'primary') {
        return '#ff9c99';
      }
      // Copy All button in darkUITheme uses same hover color as Display button
      if (props.scheme === 'darkUITheme' && props.children === 'Copy All') {
        return '#ff9c99';
      }
      // Merge, Copy, Save buttons in darkUITheme use blue hover
      if (
        props.scheme === 'darkUITheme' &&
        (props.children === 'Merge selected' ||
          props.children === 'Copy selected' ||
          props.children === 'Save')
      ) {
        return '#44a7fb';
      }
      return props.scheme === 'eighthTheme' ? '#1e7a2e !important' : 'inherit';
    }};
    border-color: ${(props) => {
      if (props.type === 'primary') {
        return '#ff9c99';
      }
      // Copy All button in darkUITheme uses blue hover border
      if (props.scheme === 'darkUITheme' && props.children === 'Copy All') {
        return '#44a7fb';
      }
      // Merge, Copy, Save buttons in darkUITheme use blue hover border
      if (
        props.scheme === 'darkUITheme' &&
        (props.children === 'Merge selected' ||
          props.children === 'Copy selected' ||
          props.children === 'Save')
      ) {
        return '#44a7fb';
      }
      return props.scheme === 'eighthTheme' ? '#1e7a2e !important' : 'inherit';
    }};
    color: ${(props) => {
      if (props.type === 'primary') {
        return '#fff !important';
      }
      // Copy All button in darkUITheme uses white text on hover
      if (props.scheme === 'darkUITheme' && props.children === 'Copy All') {
        return '#fff !important';
      }
      // Merge, Copy, Save buttons in darkUITheme use white text on hover
      if (
        props.scheme === 'darkUITheme' &&
        (props.children === 'Merge selected' ||
          props.children === 'Copy selected' ||
          props.children === 'Save')
      ) {
        return '#fff !important';
      }
      // Merge selected, Copy selected, Save buttons in deepNavyTheme use white text on hover
      if (
        props.scheme === 'deepNavyTheme' &&
        (props.children === 'Merge selected' ||
          props.children === 'Copy selected' ||
          props.children === 'Save')
      ) {
        return '#fff !important';
      }
      return props.scheme === 'eighthTheme' ? '#fff !important' : 'inherit';
    }};
  }
`;

export const SaveButtonDiv = styled('div')`
  display: flex;
  height: -webkit-fill-available;
  align-items: center;
  justify-content: end;
`;

export const ActionButtons = styled(Card)`
  .ant-card-body {
    display: flex;
    gap: 20px;
  }
`;

export const ContextCard = styled(Card)`
  margin-top: 12px;
  border-radius: 10px;
  border: 1px solid #f0f0f0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  width: max-content;
  margin: auto;

  .ant-card-head {
    font-size: 13px;
    font-weight: 600;
    padding: 8px 12px;
    min-height: 36px;
  }

  .ant-card-body {
    padding: 10px 12px;
    width: max-content;
  }
`;

export const ContextRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 12px;
  color: #374151;

  b {
    font-size: 12px;
    color: #111827;
    font-weight: 600;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #f3f4f6;
  }

  span {
    padding: 0 10px 0 0;
  }
`;

export const EmptyState = styled.div`
  font-size: 13px;
  text-align: center;
  padding: 12px 0;
`;

export const ContextRowFlex = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  align-items: center;
  padding-left: 24px;
`;

export const ContextCell = styled.div`
  display: flex;
  flex-direction: column;

  span {
    font-size: 12px;
    opacity: 0.6;
  }

  b {
    font-size: 14px;
    font-weight: 600;
  }
`;

export const EqualTransfer = styled(Transfer)`
  width: 100%;

  .ant-transfer {
    display: flex;
    width: 100%;
  }

  .ant-transfer-list {
    flex: 1 1 0 !important;
    width: 0 !important;
  }

  .ant-transfer-list-body {
    height: 100%;
  }

  .ant-transfer-list-content-item {
    height: 32px;
    display: flex;
    align-items: center;
  }

  .transfer-item {
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const SelectedContextBox = styled.div<{ scheme: string }>`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 16px;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${({ scheme }) => (scheme === 'dark' ? '#1f1f1f' : '#fafafa')};
`;

export const ContextItem = styled.div`
  padding: 4px 10px;
  border-radius: 5px;
  background: rgba(0, 0, 0, 0.04);
  font-size: 12px;
  line-height: 18px;
  max-width: 100%;
  word-break: break-word;
`;
